import React, { useCallback, useContext, useEffect } from 'react';
import Grid from "@mui/material/Grid";
import Paper from "@mui/material/Paper";
import Typography from "@mui/material/Typography";
import AppDrawer from "../AppDrawer/AppDrawer";
import { Box, Card, CardHeader, Fab } from "@mui/material";
import List from "@mui/material/List";
import ListItem from "@mui/material/ListItem";
import ListItemText from "@mui/material/ListItemText";
import AddIcon from "@mui/icons-material/Add";
import PriorityListInput from "./PriorityListInput";
import PriorityListEdit from "./PriorityListEdit";
import IconButton from "@mui/material/IconButton";
import MoreVertIcon from '@mui/icons-material/MoreVert';
import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";
import { AuthContext } from "../../context/authContext";
import frugalServicesClient from "../../utils/frugal-services-client";

export default function Priorities(props) {
    const { setPasswordExpired } = useContext(AuthContext);
    const [priorityLists, setPriorityLists] = React.useState([]);
    const [loading, setLoading] = React.useState(true);
    const [openListInputModal, setOpenListInputModal] = React.useState(false);
    const [openListEditModal, setOpenListEditModal] = React.useState(false);
    const [anchorEl, setAnchorEl] = React.useState(null);
    const [selectedList, setSelectedList] = React.useState(null);
    const [editList, setEditList] = React.useState(null);

    const handleOpenListInputModal = () => setOpenListInputModal(true);
    const handleCloseListInputModal = () => {
        setOpenListInputModal(false);
        setLoading(true);
    }
    const handleCloseListEditModal = () => {
        setOpenListEditModal(false);
        setLoading(true);
    }

    useEffect(() => {
        if (loading) {
            (async () => {
                const response = await frugalServicesClient.getPriorityLists();
                if (response.status === 401) {
                    setPasswordExpired(true);
                }
                const priorityLists = await response.json();
                setLoading(false);
                setPriorityLists(priorityLists);
            })();
        }
    }, [loading, setPasswordExpired]);

    const handleMenuClick = (event, list) => {
        setAnchorEl(event.currentTarget);
        setSelectedList(list);
        setEditList(list);
    };

    const handleMenuClose = () => {
        setAnchorEl(null);
        setSelectedList(null);
    };

    const handleEdit = useCallback(() => {
        setOpenListEditModal(true);
        handleMenuClose();
    }, []);

    const handleDelete = () => {
        if (!selectedList) return;

        frugalServicesClient.deletePriorityList(selectedList.id)
            .then(response => {
                if (response.status === 401) {
                    setPasswordExpired(true);
                }
                setPriorityLists(prevLists => prevLists.filter(list => list.id !== selectedList.id));
                handleMenuClose();
            })
            .catch(error => {
                console.error('Error deleting list:', error);
            });
    };

    const calculateTotal = useCallback((items) => {
        if (!Array.isArray(items)) {
            return 0;
        }
        return items.reduce((sum, item) => sum + (parseFloat(item.price) || 0), 0);
    }, []);

    return (
        <Box sx={{ display: 'flex', paddingTop: '64px' }}>
            <AppDrawer />
            <Paper sx={{ p: 3, width: '100%', flexGrow: 1, m: 2 }}>
                <Typography variant="h4" gutterBottom>Prioritize Your Goals</Typography>
                <Grid container spacing={3} sx={{ mt: 2 }}>
                    {(!priorityLists || priorityLists.length === 0) && !loading && (
                        <Grid size={{xs: 12}} sx={{ textAlign: 'center', py: 5 }}>
                            <Typography variant="h6" color="text.secondary">
                                No priority lists yet.
                            </Typography>
                            <Typography variant="body1" color="text.secondary">
                                Click the button below to add your first one!
                            </Typography>
                        </Grid>
                    )}
                    {priorityLists.map((priorityList) => (
                        <Grid size={{ xs: 12, sm: 6, md: 4 }} key={priorityList.id}>
                            <Card>
                                <CardHeader
                                    title={priorityList.title}
                                    subheader={`Total: ${new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(calculateTotal(priorityList.items))}`}
                                    action={
                                        <IconButton aria-label="settings" onClick={(event) => handleMenuClick(event, priorityList)}>
                                            <MoreVertIcon />
                                        </IconButton>
                                    }
                                />
                                <Menu
                                    anchorEl={anchorEl}
                                    open={Boolean(anchorEl) && selectedList?.id === priorityList.id}
                                    onClose={handleMenuClose}
                                >
                                    <MenuItem onClick={handleEdit}>Edit</MenuItem>
                                    <MenuItem onClick={handleDelete}>Delete</MenuItem>
                                </Menu>
                                <List sx={{ maxHeight: 300, overflowY: 'auto', width: '100%', scrollbarWidth: 'thin' }}>
                                    {priorityList.items.sort((a, b) => a.rank - b.rank).map((item) => (
                                        <ListItem key={item.id}>
                                            <ListItemText
                                                primary={item.description}
                                                secondary={`$${item.price}`}
                                            />
                                        </ListItem>
                                    ))}
                                </List>
                            </Card>
                        </Grid>
                    ))}
                </Grid>
            </Paper>
            <Fab
                variant="extended"
                onClick={handleOpenListInputModal}
                color="primary"
                aria-label="add priority list"
                sx={{ position: 'fixed', bottom: 32, right: 32 }}
            >
                <AddIcon sx={{ mr: 1 }} />
                New List
            </Fab>
            <PriorityListInput open={openListInputModal} closeHandler={handleCloseListInputModal} />
            <PriorityListEdit
                open={openListEditModal}
                closeHandler={handleCloseListEditModal}
                id={editList?.id}
                initialTitle={editList?.title}
                initialItems={editList?.items}
            />
        </Box>
    );
}
